import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { worldPosToClientPos } from '@/editor/utils';
import styles from './linearDimensionTextInfo.module.scss';
import { ITextModelInfo } from '../textModel';
import debounce from 'lodash-es/debounce';

interface ILinearDimensionTextInfoOptions {
  onDelete?: () => void;
}

export interface ILinearDimensionTextInfo extends Omit<ITextModelInfo, 'editable' | 'trapFocus' | 'max'> {
  rotation?: number;
}

export class LinearDimensionTextInfo {
  private _container: HTMLElement;
  private _closeButtonEl: HTMLButtonElement;
  private _position: IPoint2d = { x: 0, y: 0 };
  private _onDelete?: () => void;

  constructor(options: ILinearDimensionTextInfoOptions = {}) {
    this._onDelete = options.onDelete;
    this._container = document.createElement('div');
    this._container.classList.add(styles.linearDimensionTextInfo);

    this._closeButtonEl = document.createElement('button');
    this._closeButtonEl.type = 'button';
    this._closeButtonEl.classList.add(styles.closeButton);
    this._closeButtonEl.setAttribute('aria-label', 'Delete linear dimension');
    this._closeButtonEl.addEventListener('mousedown', this._stopCloseButtonEvent);
    this._closeButtonEl.addEventListener('mouseup', this._stopCloseButtonEvent);
    this._closeButtonEl.addEventListener('click', this._handleCloseButtonClick);

    globalStores.editorStateStore.rulerContainer.appendChild(this._container);
  }

  public viewChange = () => {
    this.setVisible(false);
    this._debounceUpdatePosition();
  };

  private _debounceUpdatePosition = debounce(() => this.updatePosition(), 300);

  private _addCloseButton() {
    let hasCloseButton = false;
    for (const element of this._container.children) {
      if ((element as HTMLElement).tagName.toLocaleLowerCase() === 'button') {
        hasCloseButton = true;
        break;
      }
    }

    if (!hasCloseButton) this._container.appendChild(this._closeButtonEl);
  }

  createModel(textInfo: ILinearDimensionTextInfo) {
    const textEl = document.createElement('span');
    textEl.classList.add('ruler-text');
    textEl.innerText = textInfo.value;

    this._container.dataset.x = `${textInfo.position.x}`;
    this._container.dataset.y = `${textInfo.position.y}`;
    if (textInfo.rotation) {
      this._container.dataset.rotation = `${textInfo.rotation}`;
    }

    this._container.appendChild(textEl);
    this._addCloseButton();
    this.updatePosition();
  }

  updatePosition() {
    for (const element of this._container.children) {
      if (element.tagName.toLocaleLowerCase() !== 'span') continue;

      const x = parseFloat(this._container.dataset.x || '0');
      const y = parseFloat(this._container.dataset.y || '0');
      const rotation = this._container.dataset.rotation ? parseFloat(this._container.dataset.rotation) : undefined;

      const {
        renderer,
        scene2d: { camera }
      } = globalStores.editorStateStore.sceneInfo;

      this._position = worldPosToClientPos({ x, y }, camera, renderer);
      this._container.style.transform = `translate(${this._position.x}px, ${this._position.y}px) translate(-50%, -50%)${rotation ? ` rotate(${(rotation * 180) / Math.PI}deg)` : ''}`;
    }

    this.setVisible(true);
  }

  setSelected(selected: boolean) {
    this._container.setAttribute('data-selected', selected ? 'true' : 'false');
  }

  setHover(hover: boolean) {
    this._container.setAttribute('data-hover', hover ? 'true' : 'false');
  }

  setVisible(visible: boolean) {
    this._container.style.display = visible ? 'block' : 'none';
  }

  private _stopCloseButtonEvent = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  private _handleCloseButtonClick = (e: MouseEvent) => {
    this._stopCloseButtonEvent(e);
    this._onDelete?.();
  };

  clear() {
    this._container.replaceChildren();
  }

  dispose() {
    this._debounceUpdatePosition.cancel();
    this.clear();
    this._closeButtonEl.removeEventListener('mousedown', this._stopCloseButtonEvent);
    this._closeButtonEl.removeEventListener('mouseup', this._stopCloseButtonEvent);
    this._closeButtonEl.removeEventListener('click', this._handleCloseButtonClick);
    this._container.parentNode?.removeChild(this._container);
  }
}
